import { HrDayBreakdown, HrRequestStatus } from '../../shared/models/hr.model';

// Visual language copied from FinanceRequestsModule (the other self-service request flow), kept
// module-local because modules never import from each other.
export const CONTENT = 'max-w-[1440px] mx-auto px-[clamp(16px,3vw,48px)]';
export const PAGE = 'min-h-[calc(100vh-45px)] bg-neutral-0 font-gilroy-regular text-neutral-500';
export const PAGE_TITLE =
  'm-0 text-[clamp(26px,2.6vw,40px)] leading-tight font-gilroy-bold text-neutral-500';
export const SECTION_TITLE =
  'm-0 mb-[20px] text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500';
export const CHIP =
  'inline-flex items-center rounded-full px-[10px] py-[3px] text-[12px] font-gilroy-medium whitespace-nowrap';
export const INPUT =
  'h-[40px] w-full min-w-0 rounded-[10px] border border-gray-200 bg-neutral-0 px-[12px] text-[13px] text-neutral-500 hover:border-neutral-500 focus:border-neutral-500 outline-none transition-colors';
export const TEXTAREA =
  'min-h-[88px] w-full rounded-[10px] border border-gray-200 bg-neutral-0 px-[12px] py-[10px] text-[13px] text-neutral-500 hover:border-neutral-500 focus:border-neutral-500 outline-none transition-colors';
export const LABEL =
  'flex min-w-0 flex-col gap-[6px] text-[12px] font-gilroy-medium text-neutral-300';
export const FIELD_ERROR = 'text-[12px] font-gilroy-regular text-scarlet';
export const TABLE_WRAP = 'overflow-x-auto rounded-[12px] border border-gray-200';
export const TH =
  'px-[16px] py-[12px] text-left text-[12px] font-gilroy-medium text-neutral-300 whitespace-nowrap';
export const TD = 'px-[16px] py-[14px] text-[14px] text-neutral-500 align-middle';
export const EMPTY =
  'rounded-[16px] bg-creams-100 px-[24px] py-[32px] text-center text-[15px] text-neutral-300';
export const BTN_PRIMARY =
  'h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] bg-primary-500 text-neutral-0 text-[14px] font-gilroy-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
export const BTN_SECONDARY =
  'h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] border border-gray-200 bg-neutral-0 text-[14px] font-gilroy-medium text-neutral-500 hover:border-neutral-500 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
export const BTN_SMALL =
  'h-[32px] inline-flex items-center px-[12px] rounded-[10px] border border-gray-200 bg-neutral-0 text-[13px] font-gilroy-medium text-neutral-500 hover:border-neutral-500 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
export const BTN_SMALL_PRIMARY =
  'h-[32px] inline-flex items-center px-[12px] rounded-[10px] bg-primary-500 text-neutral-0 text-[13px] font-gilroy-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
export const BTN_SMALL_DANGER =
  'h-[32px] inline-flex items-center px-[12px] rounded-[10px] border border-scarlet/30 bg-neutral-0 text-[13px] font-gilroy-medium text-scarlet hover:bg-scarlet/10 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';
export const BTN_DANGER =
  'h-[44px] inline-flex items-center gap-[6px] px-[16px] rounded-[12px] bg-scarlet text-neutral-0 text-[14px] font-gilroy-medium cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed';

export const STATUS_CHIP: Record<HrRequestStatus, string> = {
  requested: 'bg-creams-600 text-creams-900',
  approved: 'bg-primary-50 text-primary-700',
  rejected: 'bg-scarlet/10 border border-scarlet/20 text-scarlet',
  cancelled: 'bg-neutral-50 text-neutral-400'
};

export const BREAKDOWNS: HrDayBreakdown[] = ['full_day', 'first_half', 'second_half'];

const pad = (n: number) => String(n).padStart(2, '0');

/** Today as `YYYY-MM-DD` in the browser's zone (staff are in Cairo, as the services are). */
export const todayIso = (now = new Date()) =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

/**
 * Whether the employee may cancel: pending (withdrawn upstream), or approved and not started
 * yet (cancelled upstream). Mirrors the BFF's rule for LEAVE so the button never offers a 400;
 * WFH allows pending only, which the caller checks.
 */
export function canCancel(
  request: { status: HrRequestStatus; startDate: string },
  today = todayIso()
): boolean {
  if (request.status === 'requested') return true;
  return request.status === 'approved' && !!request.startDate && request.startDate > today;
}

/** `2026-11-02` -> `2 Nov 2026`, without passing a calendar day through a timezone. */
export function formatDay(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!match) return iso || '—';
  const at = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatRange(start: string, end: string): string {
  if (!end || start === end) return formatDay(start);
  return `${formatDay(start)} – ${formatDay(end)}`;
}

export const formatDateTime = (at?: Date) =>
  at ? at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export interface LeaveFormValues {
  leaveTypeId: string;
  startDate: string;
  startDateBreakdown: HrDayBreakdown;
  endDate: string;
  endDateBreakdown: HrDayBreakdown;
  description: string;
}

export interface WfhFormValues {
  startDate: string;
  endDate: string;
  description: string;
}

/** Error keys (HR_MODULE.FORM.ERRORS.*) per field; empty when the form can be sent. */
export type FormErrors = Partial<Record<string, string>>;

function validateRange(startDate: string, endDate: string): FormErrors {
  const errors: FormErrors = {};
  if (!startDate) errors.startDate = 'START_DATE';
  if (!endDate) errors.endDate = 'END_DATE';
  else if (startDate && endDate < startDate) errors.endDate = 'END_BEFORE_START';
  return errors;
}

export function validateLeave(values: LeaveFormValues): FormErrors {
  const errors = validateRange(values.startDate, values.endDate);
  if (!values.leaveTypeId) errors.leaveTypeId = 'LEAVE_TYPE';
  if (!values.description.trim()) errors.description = 'DESCRIPTION';
  return errors;
}

/** Description is optional here: a WFH day rarely needs explaining, and Horilla allows none. */
export function validateWfh(values: WfhFormValues): FormErrors {
  return validateRange(values.startDate, values.endDate);
}
